<template>
  <paginated-table
    id="admin-communities-table"
    ref="table"
    endpoint="communities"
    :fetch-params="{ for: 'admin' }"
    :columns="columns"
    :label="$tc('communities.community', 2)"
    :extra-filters="extraFilters"
    :count-relations="['invitations']"
    sync-filters-with-url
    row-link
  >
    <template #head-buttons>
      <icon-button v-if="canCreateCommunity" :to="`/admin/communities/new`" role="add">
        {{ $t("communities.create_community") | capitalize }}
      </icon-button>

      <icon-button to="/communities" icon="map" variant="ghost-secondary">
        {{ $t("views.admin.communities.map") | capitalize }}
      </icon-button>
    </template>

    <template #cell(actions)="{ item }">
      <admin-list-actions
        slug="communities"
        :item-name="item.name"
        :item="item"
        :destroy-params="destroyParams"
        @change="$refs.table.refresh"
        @destroy-hidden="destroyParams = initialDestroyParams()"
      >
        <template #destroy-question="{ hide }">
          <div class="communities-destroy-form">
            <p>
              <strong>{{
                $t("views.admin.communities.archive.title", { name: item.name })
              }}</strong>
            </p>

            <forms-validated-input
              v-if="item.users_count || item.loans_count || item.invitations_count"
              class="mt-2"
              :value="destroyParams.merge_destination"
              type="relation"
              name="merge_destination"
              :query="{
                slug: 'communities',
                value: 'id',
                text: 'name',
                params: {
                  '!id': item.id,
                },
              }"
              :label="$t('views.admin.communities.archive.transfer_data.title')"
              :description="$t('views.admin.communities.archive.transfer_data.description')"
              @relation="
                (community) => {
                  if (!community) {
                    destroyParams = initialDestroyParams();
                  } else {
                    destroyParams.merge_destination = community.id;
                  }
                }
              "
            />

            <b-form-group
              v-if="item.loans_count"
              :description="$t('views.admin.communities.archive.loans.description')"
            >
              <b-form-checkbox
                v-model="destroyParams.merge_loans"
                :disabled="!destroyParams.merge_destination"
              >
                <i18n path="views.admin.communities.archive.loans.title">
                  <template #loan_count_url>
                    <b-link :to="`/admin/loans?community_id=${item.id}`" @click="hide">
                      ({{ item.loans_count }})
                    </b-link>
                  </template>
                </i18n>
              </b-form-checkbox>
            </b-form-group>
            <p v-else>{{ $t("views.admin.communities.archive.loans.none") }}</p>

            <b-form-group
              v-if="item.users_count"
              :description="$t('views.admin.communities.archive.users.description')"
            >
              <b-form-checkbox
                v-model="destroyParams.merge_users"
                :disabled="!destroyParams.merge_destination"
              >
                <i18n path="views.admin.communities.archive.users.title">
                  <template #user_count_url>
                    <b-link :to="`/admin/communities/${item.id}`" @click="hide">
                      ({{ item.users_count }})
                    </b-link>
                  </template>
                </i18n>
              </b-form-checkbox>
            </b-form-group>
            <p v-else>{{ $t("views.admin.communities.archive.users.none") }}</p>
            <b-form-group
              v-if="destroyParams.merge_users"
              :description="$t('views.admin.communities.archive.users.status.description')"
            >
              <b-form-checkbox v-model="destroyParams.merge_user_status">
                {{ $t("views.admin.communities.archive.users.status.title") }}
              </b-form-checkbox>
            </b-form-group>

            <b-form-group
              v-if="item.invitations_count"
              :description="$t('views.admin.communities.archive.invitations.description')"
            >
              <b-form-checkbox
                v-model="destroyParams.merge_invitations"
                :disabled="!destroyParams.merge_destination"
              >
                <i18n path="views.admin.communities.archive.invitations.title">
                  <template #invitation_count_url>
                    <b-link :to="`/admin/invitations?community_id=${item.id}`" @click="hide">
                      ({{ item.invitations_count }})
                    </b-link>
                  </template>
                </i18n>
              </b-form-checkbox>
            </b-form-group>
            <p v-else>{{ $t("views.admin.communities.archive.invitations.none") }}</p>
          </div>
        </template>
        <template #restore-question>
          <p>
            <strong>{{ $t("views.admin.communities.restore.title", { name: item.name }) }}</strong>
          </p>
        </template>
      </admin-list-actions>
    </template>
  </paginated-table>
</template>

<script>
import AdminListActions from "@/components/Admin/ListActions.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import { isGlobalAdmin } from "@/helpers/permissions/users";
import { canSeeDeletedCommunity } from "@/helpers/permissions/communities";
import { capitalize } from "@/helpers/filters";

function initialDestroyParams() {
  return {
    merge_loans: false,
    merge_users: false,
    merge_destination: null,
    merge_user_status: false,
    merge_invitations: false,
  };
}

export default {
  name: "AdminCommunities",
  components: {
    IconButton,
    FormsValidatedInput,
    PaginatedTable,
    AdminListActions,
  },
  data() {
    const data = {
      destroyParams: initialDestroyParams(),
      selected: [],
      columns: [
        new Column("id", this.$t("lists.id"), "id"),
        new Column("name", capitalize(this.$t("communities.fields.name"))),
        Column.withSelect("type", capitalize(this.$t("communities.fields.type")), [
          { value: null, label: this.$t("communities.fields.types.null") },
          {
            value: "borough",
            label: this.$t("communities.fields.types.borough"),
            variant: "success",
          },
          {
            value: "private",
            label: this.$t("communities.fields.types.private"),
            variant: "primary",
          },
        ]),
        Column.withRelationCount("users_count", this.$t("communities.fields.users_count")),
        Column.withRelationCount("loans_count", this.$t("communities.fields.loans_count")),
      ],
      extraFilters: [
        new Filter(
          "required_proofs",
          this.$tc("communities.fields.required_proofs.title", 1),
          "select",
          [
            { value: null, label: this.$t("communities.fields.required_proofs.options.null.any") },
            {
              value: "identity",
              label: this.$t("communities.fields.required_proofs.options.identity"),
            },
            {
              value: "residency",
              label: this.$t("communities.fields.required_proofs.options.residency"),
            },
            {
              value: "custom",
              label: this.$t("communities.fields.required_proofs.options.custom"),
            },
          ]
        ),
      ],
    };

    if (canSeeDeletedCommunity(this.$store.state.user)) {
      data.columns.push(
        new Column("deleted_at", this.$t("views.admin.communities.columns.archive_date"), "date", {
          showByDefault: false,
        })
      );
      data.extraFilters.push(
        new Filter("is_deleted", this.$t("views.admin.communities.filters.archived"), "boolean")
      );
    }
    return data;
  },
  computed: {
    canCreateCommunity() {
      // Global admins only for the moment.
      const loggedInUser = this.$store.state.user;
      return isGlobalAdmin(loggedInUser);
    },
  },
  methods: {
    initialDestroyParams,
  },
};
</script>

<style lang="scss">
.communities_destroy_form .form-group {
  margin-bottom: 0.5rem;
}
</style>
